import { MasterAccountPermission } from '@insio/shared-types';
import { navigation } from '@/navigation';

const OFFICE = [MasterAccountPermission.SETTINGS_MANAGE] as const;
const PEOPLE = [
  MasterAccountPermission.PEOPLE_MANAGE,
  MasterAccountPermission.ASSIGNMENTS_MANAGE
] as const;
const BILLING = [MasterAccountPermission.BILLING_MANAGE] as const;

/**
 * Master permissions that open each board section and settings section, any one of them enough —
 * the menu, the settings menu and the board home read the same.
 */
export const MASTER_ACCOUNT_BOARD_SECTION_PERMISSIONS = {
  clients: [MasterAccountPermission.BOARD_READ],
  office: OFFICE,
  people: PEOPLE,
  billing: BILLING,
  // Any section of Nastavení kanceláře; Bridge and the templates also need their menu hooks.
  settings: [...OFFICE, ...PEOPLE, ...BILLING, MasterAccountPermission.INTEGRATIONS_MANAGE]
} as const satisfies Record<string, readonly MasterAccountPermission[]>;

export type MasterAccountBoardSection = keyof typeof MASTER_ACCOUNT_BOARD_SECTION_PERMISSIONS;

/** Whether the caller may open a board section. */
export function canOpenMasterAccountBoardSection(
  section: MasterAccountBoardSection,
  hasMasterPermission: (permission: MasterAccountPermission) => boolean
): boolean {
  return MASTER_ACCOUNT_BOARD_SECTION_PERMISSIONS[section].some(permission =>
    hasMasterPermission(permission)
  );
}

/** Sections in the order the board home falls back through them. */
const HOME_ORDER = [
  ['clients', navigation.accountRelations()],
  ['settings', navigation.masterAccountSettings()]
] as const satisfies readonly (readonly [MasterAccountBoardSection, string])[];

/** The first board section the caller may open, or null when they may open none. */
export function masterAccountBoardHomePath(
  hasMasterPermission: (permission: MasterAccountPermission) => boolean
): string | null {
  return (
    HOME_ORDER.find(([section]) =>
      canOpenMasterAccountBoardSection(section, hasMasterPermission)
    )?.[1] ?? null
  );
}
